import { useState } from "react";
import { ArrowRight, Check } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useModals } from "@/components/ModalContext";
import { PRODUCTOS, VALORES } from "@/constants/site";
import { Reveal } from "@/components/Reveal";

const TAMANOS = {
  panes: "md:col-span-2 lg:col-span-7 h-[400px] lg:h-[460px]",
  salados: "lg:col-span-5 h-[400px] lg:h-[460px]",
  arabes: "lg:col-span-4 h-[360px]",
  pasteleria: "lg:col-span-4 h-[360px]",
  bolleria: "lg:col-span-4 h-[360px]",
  sushi: "lg:col-span-6 h-[400px]",
  trampantojos: "md:col-span-2 lg:col-span-6 h-[400px]",
};

export const Productos = () => {
  const [selected, setSelected] = useState(null);
  const { openContact } = useModals();

  const consultar = () => {
    const producto = selected;
    setSelected(null);
    openContact(`Hola, me gustaría pedir información sobre: ${producto.titulo}.`);
  };

  return (
    <section id="productos" data-testid="products-section" className="bg-crema/40 py-20 sm:py-28 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
        <Reveal className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.3em] text-cafe">
              <span aria-hidden className="h-px w-9 bg-cafe/60" />
              Productos
            </p>
            <h2 className="mt-6 max-w-2xl font-serif text-3xl leading-[1.14] tracking-tight text-tinta sm:text-4xl lg:text-[2.9rem]">
              Nuestra selección,{" "}
              <em className="font-garamond font-medium italic text-cafe">elaborada cada día.</em>
            </h2>
          </div>
          <p className="max-w-sm text-sm leading-relaxed text-tinta/60 sm:text-base">
            Siete familias de producto, una misma obsesión: hacerlo bien, a mano y cada día.
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-12 lg:gap-8">
          {PRODUCTOS.map((p, i) => (
            <Reveal
              key={p.id}
              delay={(i % 3) * 0.08}
              className={`${TAMANOS[p.id]} md:col-span-1`}
            >
              <article
                data-testid={`product-card-${p.id}`}
                onClick={() => setSelected(p)}
                className="group relative h-full w-full cursor-pointer overflow-hidden rounded-3xl bg-chocolate shadow-[0_18px_40px_rgba(58,36,24,0.12)]"
              >
                <img
                  src={p.imagen}
                  alt={p.titulo}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105"
                  data-zoomable
                />
                <div
                  aria-hidden
                  className="absolute inset-0 bg-gradient-to-t from-chocolate via-chocolate/60 to-transparent"
                />
                <div className="relative flex h-full flex-col justify-end p-6 sm:p-8">
                  <span className="font-garamond text-lg italic text-crema/70">{p.numero}</span>
                  <h3 className="mt-2 max-w-md font-serif text-2xl text-crema sm:text-3xl">
                    {p.titulo}
                  </h3>
                  <p className="mt-2 max-w-sm text-sm leading-relaxed text-crema/75">
                    {p.descripcion}
                  </p>
                  <button
                    data-testid={`product-card-discover-${p.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelected(p);
                    }}
                    className="mt-5 inline-flex w-fit items-center gap-2 rounded-full bg-crema px-5 py-2.5 text-sm font-semibold text-chocolate transition-all duration-300 hover:bg-fondo"
                  >
                    Descubrir
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent
          data-testid="product-dialog"
          className="overflow-hidden rounded-3xl p-0 sm:max-w-3xl"
        >
          {selected && (
            <div className="grid sm:grid-cols-2">
              <div className="bg-arena/20">
                <img
                  src={selected.imagen}
                  alt={selected.titulo}
                  className="h-52 w-full object-cover sm:h-72"
                  loading="lazy"
                />
                {selected.imagenes && selected.imagenes.length > 0 && (
                  <div
                    data-testid="product-dialog-gallery"
                    className="grid grid-cols-2 gap-2 p-3"
                  >
                    {selected.imagenes.map((src) => (
                      <img
                        key={src}
                        src={src}
                        alt={selected.titulo}
                        loading="lazy"
                        className="h-24 w-full rounded-xl object-cover"
                      />
                    ))}
                  </div>
                )}
              </div>
              <div className="flex flex-col p-6 sm:p-8">
                <span className="font-garamond text-lg italic text-cafe/70">
                  {selected.numero}
                </span>
                <DialogTitle className="mt-2 text-left font-serif text-2xl text-tinta sm:text-3xl">
                  {selected.titulo}
                </DialogTitle>
                <p className="mt-4 text-sm leading-relaxed text-tinta/70 sm:text-base">
                  {selected.descripcion}
                </p>
                <ul className="mt-6 space-y-3">
                  {VALORES.map((v) => (
                    <li key={v.titulo} className="flex items-center gap-3 text-sm text-tinta/75">
                      <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-arena/60">
                        <Check className="h-3.5 w-3.5 text-chocolate" />
                      </span>
                      {v.titulo}
                    </li>
                  ))}
                </ul>
                <div className="mt-auto flex flex-col gap-3 pt-8 sm:flex-row">
                  <Button
                    data-testid="product-dialog-contact-button"
                    onClick={consultar}
                    className="rounded-full px-6"
                  >
                    Consultar por este producto
                  </Button>
                  <Button
                    variant="ghost"
                    data-testid="product-dialog-close-button"
                    onClick={() => setSelected(null)}
                    className="rounded-full px-6"
                  >
                    Cerrar
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default Productos;
